import { createContext, useContext, useState, useCallback } from 'react'

const BookingContext = createContext(null)

const INITIAL = {
  step: 1,           // 1=service 2=staff 3=datetime 4=details 5=payment 6=confirm
  services:   [],    // selected service objects
  staff:      null,  // selected staff object
  date:       null,  // JS Date
  timeSlot:   null,  // "HH:MM"
  customer: { name: '', email: '', phone: '', notes: '' },
  coupon:     null,
  paymentMethod: 'online', // 'online' | 'at_salon'
  appointmentId: null,
}

export function BookingProvider({ children }) {
  const [booking, setBooking] = useState(INITIAL)

  const setStep = useCallback((s) => setBooking((b) => ({ ...b, step: s })), [])
  const nextStep = useCallback(() => setBooking((b) => ({ ...b, step: b.step + 1 })), [])
  const prevStep = useCallback(() => setBooking((b) => ({ ...b, step: Math.max(1, b.step - 1) })), [])

  const setServices  = useCallback((v) => setBooking((b) => ({ ...b, services: v })), [])
  const setStaff     = useCallback((v) => setBooking((b) => ({ ...b, staff: v })), [])
  const setDate      = useCallback((v) => setBooking((b) => ({ ...b, date: v, timeSlot: null })), [])
  const setTimeSlot  = useCallback((v) => setBooking((b) => ({ ...b, timeSlot: v })), [])
  const setCustomer  = useCallback((v) => setBooking((b) => ({ ...b, customer: { ...b.customer, ...v } })), [])
  const setCoupon    = useCallback((v) => setBooking((b) => ({ ...b, coupon: v })), [])
  const setPayMethod = useCallback((v) => setBooking((b) => ({ ...b, paymentMethod: v })), [])
  const setAppointmentId = useCallback((v) => setBooking((b) => ({ ...b, appointmentId: v })), [])

  const reset = useCallback(() => setBooking(INITIAL), [])

  // Derived
  const totalDuration = booking.services.reduce((acc, s) => acc + (s.duration_minutes || 0), 0)
  const subtotal      = booking.services.reduce((acc, s) => acc + parseFloat(s.price || 0), 0)
  const discount      = booking.coupon
    ? booking.coupon.type === 'percent'
      ? (subtotal * booking.coupon.value) / 100
      : parseFloat(booking.coupon.value)
    : 0
  const total = Math.max(0, subtotal - discount)

  return (
    <BookingContext.Provider value={{
      booking,
      setStep, nextStep, prevStep,
      setServices, setStaff, setDate, setTimeSlot,
      setCustomer, setCoupon, setPayMethod, setAppointmentId,
      reset,
      totalDuration, subtotal, discount, total,
    }}>
      {children}
    </BookingContext.Provider>
  )
}

export function useBooking() {
  const ctx = useContext(BookingContext)
  if (!ctx) throw new Error('useBooking must be used inside <BookingProvider>')
  return ctx
}
